import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../components/ui/sonner';
import { Lock, Image, Upload, Eye, EyeOff } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Settings = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [currentBackground, setCurrentBackground] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const navigate = useNavigate();
  const { t, isRtl } = useLanguage();

  useEffect(() => {
    fetchUserRole();
    fetchCurrentBackground();
  }, []);

  const fetchUserRole = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserRole(response.data.role);
    } catch (error) {
      console.error('Failed to fetch user role');
    }
  };

  const fetchCurrentBackground = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/settings/login-background`);
      if (response.data.url) {
        setCurrentBackground(`${BACKEND_URL}${response.data.url}`);
      }
    } catch (error) {
      console.error('Failed to fetch background');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error(t.passwordsDoNotMatch);
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error(t.passwordMinLength);
      return;
    }

    setChangingPassword(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('current_password', currentPassword);
      formData.append('new_password', newPassword);
      
      await axios.put(`${BACKEND_URL}/api/auth/change-password`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(t.passwordChanged);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || t.somethingWentWrong);
    } finally {
      setChangingPassword(false);
    }
  };

  const handleBackgroundUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t.imageTypes);
      return;
    }

    setUploadingImage(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${BACKEND_URL}/api/settings/login-background`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(t.backgroundUpdated);
      fetchCurrentBackground();
    } catch (error) {
      toast.error(error.response?.data?.detail || t.somethingWentWrong);
    } finally {
      setUploadingImage(false);
    }
  };

  return (
    <Layout>
      <div data-testid="settings-page" className="space-y-6">
        <div className={isRtl ? 'text-right' : ''}>
          <h1 className="text-4xl font-bold text-slate-900">{t.settingsTitle}</h1>
          <p className="text-slate-600 mt-2">{t.settingsSubtitle}</p>
        </div>

        {/* Change Password Section */}
        <Card className="p-6 bg-white">
          <div className={`flex items-center gap-3 mb-6 ${isRtl ? 'flex-row-reverse' : ''}`}>
            <div className="bg-blue-100 p-3 rounded-lg">
              <Lock className="w-6 h-6 text-blue-600" />
            </div>
            <div className={isRtl ? 'text-right' : ''}>
              <h2 className="text-xl font-bold text-slate-900">{t.changePassword}</h2>
              <p className="text-sm text-slate-600">{t.updateYourPassword}</p>
            </div>
          </div>

          <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
            <div>
              <Label htmlFor="current-password" className={isRtl ? 'block text-right' : ''}>{t.currentPassword}</Label>
              <div className="relative mt-2">
                <Input
                  id="current-password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder={t.enterCurrentPassword}
                  required
                  className={isRtl ? 'text-right pr-10' : ''}
                  dir={isRtl ? 'rtl' : 'ltr'}
                />
                <button
                  type="button"
                  className={`absolute ${isRtl ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600`}
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="new-password" className={isRtl ? 'block text-right' : ''}>{t.newPassword}</Label>
              <div className="relative mt-2">
                <Input
                  id="new-password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={t.enterNewPassword}
                  required
                  className={isRtl ? 'text-right pr-10' : ''}
                  dir={isRtl ? 'rtl' : 'ltr'}
                />
                <button
                  type="button"
                  className={`absolute ${isRtl ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600`}
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirm-password" className={isRtl ? 'block text-right' : ''}>{t.confirmNewPassword}</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t.confirmPassword}
                className={`mt-2 ${isRtl ? 'text-right' : ''}`}
                dir={isRtl ? 'rtl' : 'ltr'}
                required
              />
            </div>

            <Button
              type="submit"
              disabled={changingPassword}
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              {changingPassword ? t.changingPassword : t.changePassword}
            </Button>
          </form>
        </Card>

        {/* Login Background Section - Admin Only */}
        {userRole === 'admin' && (
          <Card className="p-6 bg-white">
            <div className={`flex items-center gap-3 mb-6 ${isRtl ? 'flex-row-reverse' : ''}`}>
              <div className="bg-green-100 p-3 rounded-lg">
                <Image className="w-6 h-6 text-green-600" />
              </div>
              <div className={isRtl ? 'text-right' : ''}>
                <h2 className="text-xl font-bold text-slate-900">{t.loginBackground}</h2>
                <p className="text-sm text-slate-600">{t.customizeLoginBg}</p>
              </div>
            </div>

            <div className="space-y-4">
              {currentBackground && (
                <div className={isRtl ? 'text-right' : ''}>
                  <p className="text-sm text-slate-600 mb-2">{t.currentBackground}:</p>
                  <img
                    src={currentBackground}
                    alt="Current login background"
                    className="w-64 h-40 object-cover rounded-lg border border-slate-200"
                  />
                </div>
              )}

              <div className={isRtl ? 'text-right' : ''}>
                <Label htmlFor="background-upload">{t.uploadNewBackground}</Label>
                <div className={`mt-2 flex items-center gap-4 ${isRtl ? 'flex-row-reverse' : ''}`}>
                  <label className="cursor-pointer">
                    <input
                      id="background-upload"
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleBackgroundUpload}
                      className="hidden"
                      disabled={uploadingImage}
                    />
                    <div className={`flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors ${isRtl ? 'flex-row-reverse' : ''}`}>
                      <Upload className="w-4 h-4" />
                      <span>{uploadingImage ? t.uploadingImage : t.chooseImage}</span>
                    </div>
                  </label>
                  <span className="text-sm text-slate-500">{t.imageTypes}</span>
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default Settings;
